import React from 'react';
import ReactDOM from 'react-dom/client';
import { flushSync } from 'react-dom';
import { PosSale, PosSettings } from '../../interfaces/pos.interface';
import { formatMoney } from '../../utils/currency';
import { formatDateTime } from '../../utils/dateUtils';
import './posReceipt.css';

export type PosReceiptFormat = 'A4' | 'MM80';

interface PosReceiptProps {
    sale: PosSale;
    settings: PosSettings;
    format: PosReceiptFormat;
    // Task-6 review F7: PosSaleDetail already resolves these three ids to names (Task 5's own
    // review F5); a customer-facing receipt regressing to raw UUIDs is worse than the detail
    // screen never having fixed this. All three are optional and fall back to the raw id so this
    // component still renders something correct when no resolution was available (e.g. the
    // name-lookup itself failed -- PosSaleDetail's `loadNames` is best-effort and never blocks
    // the sale from rendering; the receipt must degrade the same way).
    storeName?: string | null;
    terminalLabel?: string | null;
    cashierName?: string | null;
}

/*
 * The receipt reprint, in both formats the counter/back-office need: A4 (office printer fallback)
 * and MM80 (the real target -- an 80mm thermal roll, fixed width, continuous length, no margins).
 *
 * Every reprint carries a REPRINT banner (R1: a reprint must never be mistakable for the original
 * tendered receipt -- see posReceipt.css's .pos-receipt__reprint-banner), and a voided sale
 * additionally carries a VOIDED banner (a reprint of a voided sale must never read as a valid
 * receipt). Both are unconditional/conditional respectively -- REPRINT always renders because
 * this component only exists for reprints (there is no "original print" path in the back office;
 * the counter app prints the original at time of sale), VOIDED only when sale.status is VOIDED.
 *
 * Every money/qty figure below is read straight off the sale DTO -- there is no client-side
 * arithmetic anywhere in this component. A reprint must never be able to disagree with what the
 * customer was actually charged, and the only way to guarantee that is to never recompute it.
 */
const PosReceipt: React.FC<PosReceiptProps> = ({
    sale, settings, format, storeName, terminalLabel, cashierName,
}) => {
    const formatClass = format === 'MM80' ? 'pos-receipt--mm80' : 'pos-receipt--a4';

    // F6: `lines`/`tenders` are ALWAYS null on a list-shaped sale (pos.interface.ts's own
    // comment on PosSale). Rendering `?? []` there silently produced a complete-looking receipt
    // with a real total and no items and no tender -- exactly the "looks complete but has no
    // basket" document the brief warned a reprint must never be. Fail loudly instead: this is
    // the one state where showing nothing is more honest than showing the sale.
    if (sale.lines == null || sale.tenders == null) {
        return (
            <div className={`pos-receipt ${formatClass}`} data-format={format}>
                <div className="pos-receipt__reprint-banner">*** REPRINT ***</div>
                <div className="pos-receipt__data-error">
                    Receipt data unavailable -- reload the sale and try again.
                </div>
            </div>
        );
    }

    const lines = sale.lines;
    const tenders = sale.tenders;

    return (
        <div className={`pos-receipt ${formatClass}`} data-format={format}>
            <div className="pos-receipt__reprint-banner">*** REPRINT ***</div>

            {sale.status === 'VOIDED' && (
                <div className="pos-receipt__voided-banner">*** VOIDED -- NOT A VALID RECEIPT ***</div>
            )}

            {settings.receiptHeader && (
                <div className="pos-receipt__header">{settings.receiptHeader}</div>
            )}

            <hr className="pos-receipt__hr" />

            <div className="pos-receipt__row">
                <span>Store</span>
                <span>{storeName ?? sale.storeId}</span>
            </div>
            <div className="pos-receipt__row">
                <span>Terminal</span>
                <span>{terminalLabel ?? sale.terminalId}</span>
            </div>
            <div className="pos-receipt__row">
                <span>Receipt #</span>
                <span>{sale.receiptNo}</span>
            </div>
            <div className="pos-receipt__row">
                <span>Sold</span>
                <span>{formatDateTime(sale.soldAt)}</span>
            </div>
            <div className="pos-receipt__row">
                <span>Cashier</span>
                <span>{cashierName ?? sale.cashierUserId}</span>
            </div>

            <hr className="pos-receipt__hr" />

            <table className="pos-receipt__lines-table">
                <tbody>
                    {lines.map(line => (
                        <React.Fragment key={line.id}>
                            <tr>
                                <td colSpan={2}>{line.itemName}</td>
                            </tr>
                            <tr>
                                {/* qty is BIGINT end to end -- always the plain integer, never
                                    toFixed(2)/formatMoney. */}
                                <td>{line.qty} x {formatMoney(line.unitPrice)}</td>
                                <td className="pos-receipt__right">{formatMoney(line.lineTotal)}</td>
                            </tr>
                        </React.Fragment>
                    ))}
                </tbody>
            </table>

            <hr className="pos-receipt__hr" />

            <table className="pos-receipt__totals-table">
                <tbody>
                    <tr>
                        <td>Subtotal</td>
                        <td className="pos-receipt__right">{formatMoney(sale.subtotalAmount)}</td>
                    </tr>
                    <tr>
                        <td>Discount</td>
                        <td className="pos-receipt__right">{formatMoney(sale.discountAmount)}</td>
                    </tr>
                    {/* receiptShowVatLine: false must OMIT this row entirely -- never render it
                        empty or as zero. */}
                    {settings.receiptShowVatLine && (
                        <tr>
                            <td>VAT</td>
                            <td className="pos-receipt__right">{formatMoney(sale.taxAmount)}</td>
                        </tr>
                    )}
                    <tr>
                        <td>Rounding</td>
                        <td className="pos-receipt__right">{formatMoney(sale.roundingAmount)}</td>
                    </tr>
                    <tr>
                        <td><strong>Total</strong></td>
                        <td className="pos-receipt__right"><strong>{formatMoney(sale.totalAmount)}</strong></td>
                    </tr>
                </tbody>
            </table>

            <hr className="pos-receipt__hr" />

            <table className="pos-receipt__totals-table">
                <tbody>
                    {tenders.map(tender => (
                        <tr key={tender.id}>
                            <td>{tender.method}</td>
                            <td className="pos-receipt__right">{formatMoney(tender.amount)}</td>
                        </tr>
                    ))}
                    <tr>
                        <td>Tendered</td>
                        <td className="pos-receipt__right">{formatMoney(sale.tenderTotal)}</td>
                    </tr>
                    <tr>
                        <td>Change</td>
                        <td className="pos-receipt__right">{formatMoney(sale.changeAmount)}</td>
                    </tr>
                </tbody>
            </table>

            {settings.receiptFooter && (
                <>
                    <hr className="pos-receipt__hr" />
                    <div className="pos-receipt__footer">{settings.receiptFooter}</div>
                </>
            )}

            {/* Repeated near the tail of the document: a continuous 80mm roll is usually read
                tail-first as it comes off the printer, not head-first (task-6 review F5). */}
            <div className="pos-receipt__reprint-banner-repeat">*** REPRINT ***</div>
        </div>
    );
};

export default PosReceipt;

// This app's own DOM must never carry the receipt mid-session (a full-page window.print(), the
// only existing precedent in this repo -- CustomerStatement.tsx -- prints the sidebar and app
// chrome along with the content because nothing hides the rest of the page). Printing through a
// hidden iframe, per the spec's convention for the counter app, means the receipt is rendered
// into an entirely separate document that only ever contains the receipt.
//
// posReceipt.css is imported normally by PosReceipt (above) purely so the module's rules are
// declared in one place; because every selector in that file is scoped under `.pos-receipt`
// (task-6 review F1), the rules landing in the app's own bundle are inert there -- no element in
// the main document ever carries that class. The iframe is a SEPARATE document, though, and does
// not inherit the host document's stylesheets at all, so the receipt's rules are copied across
// explicitly here -- RULE BY RULE, not sheet-by-sheet (task-6 review F10: copying whenever a
// whole stylesheet's concatenated text merely CONTAINED '.pos-receipt' copied every other rule in
// that stylesheet too, and because react-scripts concatenates the entire app into one
// main.<hash>.css, that meant copying the WHOLE app's CSS into the iframe on every reprint).
const copyReceiptStylesInto = (iframeDoc: Document): void => {
    Array.from(document.styleSheets).forEach(sheet => {
        let rules: CSSRuleList;
        try {
            rules = sheet.cssRules ?? ([] as unknown as CSSRuleList);
        } catch {
            // A cross-origin stylesheet (e.g. a Google Fonts <link>) throws on .cssRules --
            // never relevant to the receipt, always safe to skip.
            return;
        }
        const matched = Array.from(rules).filter(rule => rule.cssText.includes('.pos-receipt'));
        if (matched.length === 0) return;
        const style = iframeDoc.createElement('style');
        style.textContent = matched.map(rule => rule.cssText).join('\n');
        iframeDoc.head.appendChild(style);
    });
};

/**
 * Renders the receipt into a hidden iframe and invokes the browser print dialog on it, then tears
 * the iframe down. The app's own DOM is never touched.
 *
 * NOTE ON WHAT THIS CANNOT VERIFY: whether the browser actually honours the 80mm @page size on a
 * real thermal printer, whether the printer driver clips or scales the continuous-length layout,
 * and whether the REPRINT/VOIDED banners are legible at the print resolution/contrast a specific
 * thermal head produces are all real-paper questions this code cannot answer in this environment
 * -- only physical printer acceptance on the user's own hardware can confirm them.
 */
export const printPosReceipt = (
    sale: PosSale,
    settings: PosSettings,
    format: PosReceiptFormat,
    names?: { storeName?: string | null; terminalLabel?: string | null; cashierName?: string | null },
): void => {
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = 'none';
    document.body.appendChild(iframe);

    // F11: `afterprint` is the correct signal and fires reliably in Chrome (which blocks on
    // `print()`), but is not guaranteed everywhere a print-preview can be asynchronous. The
    // timeout fallback must not fire ahead of a still-open preview, so it is long (60s, not
    // 500ms) and is cancelled the moment `afterprint` actually arrives, so the two paths cannot
    // both tear the iframe down.
    let cleaned = false;
    let fallbackTimer: number | undefined;
    const cleanup = () => {
        if (cleaned) return;
        cleaned = true;
        if (fallbackTimer !== undefined) window.clearTimeout(fallbackTimer);
        if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
    };

    const iframeWindow = iframe.contentWindow;
    const iframeDoc = iframe.contentDocument;
    if (!iframeWindow || !iframeDoc) {
        cleanup();
        return;
    }

    const mountPoint = iframeDoc.createElement('div');
    iframeDoc.body.appendChild(mountPoint);
    copyReceiptStylesInto(iframeDoc);

    // One stylesheet serves both formats; the page size itself must still be set per format at
    // print time -- A4 keeps the browser's default page size, MM80 needs an explicit
    // continuous-roll page with no margin.
    const pageRule = format === 'MM80' ? '@page { size: 80mm auto; margin: 0; }' : '@page { size: A4; }';
    const pageStyle = iframeDoc.createElement('style');
    pageStyle.textContent = pageRule;
    iframeDoc.head.appendChild(pageStyle);

    // F2: React 18's root.render() schedules the commit -- it is NOT synchronous -- and printing
    // from an uncoordinated requestAnimationFrame raced that scheduling, so a slow render (a long
    // basket, a slow office PC) could hand the print dialog an iframe with nothing committed into
    // it yet ("prints blank"). flushSync forces the commit to complete before this call returns,
    // so by the time execution reaches iframeWindow.print() below, the DOM is guaranteed painted.
    const root = ReactDOM.createRoot(mountPoint);
    flushSync(() => {
        root.render(
            <PosReceipt
                sale={sale}
                settings={settings}
                format={format}
                storeName={names?.storeName}
                terminalLabel={names?.terminalLabel}
                cashierName={names?.cashierName}
            />,
        );
    });

    iframeWindow.addEventListener('afterprint', cleanup);
    iframeWindow.focus();
    iframeWindow.print();
    // Some browsers (Firefox) never fire afterprint on a programmatically-triggered print of an
    // iframe -- fall back to a long timeout so the hidden iframe never leaks permanently, without
    // racing an open print preview the way a short timeout would.
    fallbackTimer = window.setTimeout(cleanup, 60000);
};
